import { useState, useEffect, useCallback } from "react";
import { FiClock, FiPackage, FiRefreshCw } from "react-icons/fi";
import API_URL from './config';
import getDeviceId from "./utils/device";
import { fmtDateLong } from "./utils/formatters";

// Bump this string on every deploy — lets you confirm the refresh button
// actually pulled the new build instead of a cached one.
const APP_BUILD = "2026-08-02.2";

function BackspaceIcon() {
  return (
    <svg width={23} height={23} viewBox="0 0 24 24" fill="none"
      stroke="var(--sub)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 4H8L1 12l7 8h13a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1z" />
      <path d="M16 9l-6 6M10 9l6 6" />
    </svg>
  );
}

export default function Login({ onSuccess, businessName }) {
  const [passcode, setPasscode] = useState("");
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState("");
  const [now, setNow] = useState(new Date());
  const [refreshing, setRefreshing] = useState(false);

  const refreshApp = useCallback(async () => {
    setRefreshing(true);
    try {
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map(r => r.unregister()));
      }
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map(k => caches.delete(k)));
      }
    } finally {
      window.location.reload();
    }
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const press = useCallback((key) => {
    setError("");
    if (key === "clear") { setPasscode(""); return; }
    if (key === "back") { setPasscode(p => p.slice(0, -1)); return; }
    setPasscode(p => p.length < 6 ? p + key : p);
  }, []);

  const submit = useCallback(async (destination) => {
    if (!passcode) return;
    setLoading(destination);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ passcode, device_id: getDeviceId() }),
      });
      const data = await res.json();
      if (res.ok) {
        onSuccess(data.token, data.role, data.employee, destination);
      } else {
        setError(data.message || "Wrong passcode. Try again.");
        setPasscode("");
      }
    } catch {
      setError("Cannot connect to server.");
      setPasscode("");
    } finally {
      setLoading(null);
    }
  }, [passcode, onSuccess]);

  // Time display
  const h = now.getHours();
  const m = now.getMinutes();
  const ampm = h < 12 ? "AM" : "PM";
  const h12 = h % 12 || 12;
  const timeStr = `${String(h12).padStart(2, "0")}:${String(m).padStart(2, "0")}`;

  // Short device ID derived from the stored UUID
  const rawId = getDeviceId();
  const shortId = "DV-" + rawId.replace(/-/g, "").slice(0, 4).toUpperCase();

  const dots = Array.from({ length: 6 }, (_, i) => i < passcode.length);
  const keys = ["1","2","3","4","5","6","7","8","9","clear","0","back"];
  const canSubmit = passcode.length > 0 && !loading;

  return (
    <div style={{
      position: "fixed", inset: 0,
      background: "var(--bg)",
      display: "flex", flexDirection: "column",
      fontFamily: "var(--font-body)",
      userSelect: "none",
    }}>

      {/* ── Header ── */}
      <div style={{
        height: 64, flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 40px",
        borderBottom: "1px solid var(--hair)",
      }}>
        {/* Logo + business name */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 12,
            background: "var(--primary)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "var(--font-head)", fontWeight: 800, fontSize: 13.5,
            color: "#fff", letterSpacing: "-.02em", flexShrink: 0,
          }}>HKZ</div>
          <span style={{
            fontFamily: "var(--font-head)", fontSize: 16,
            fontWeight: 800, letterSpacing: "-.02em", color: "var(--ink)",
          }}>
            {businessName || "Hein Khant Zaw"}
          </span>
        </div>

        {/* Device + date */}
        <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, color: "var(--sub)", fontWeight: 600 }}>
            <span style={{ width: 7, height: 7, borderRadius: 99, background: "var(--success)", flexShrink: 0 }} />
            {shortId} <span style={{ color: "var(--n-300)", fontWeight: 500 }}>· build {APP_BUILD}</span>
          </div>
          <div style={{ fontSize: 13.5, color: "var(--sub)", fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>
            {fmtDateLong(now)}
          </div>
          <button
            onClick={refreshApp}
            disabled={refreshing}
            title="Refresh app (fixes blank screen after update)"
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 32, height: 32, borderRadius: 9,
              border: "1px solid var(--line)", background: "var(--surface)",
              cursor: refreshing ? "default" : "pointer", flexShrink: 0,
            }}
          >
            <FiRefreshCw size={15} color="var(--n-500)" className={refreshing ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* ── Main: left hero + right card ── */}
      <div style={{ flex: 1, display: "flex", alignItems: "center", padding: "0 56px 40px", gap: 64, minHeight: 0 }}>

        {/* Left: hero clock + context */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: "var(--accent)", marginBottom: 14 }}>
            Welcome back
          </div>
          <div style={{
            fontFamily: "var(--font-head)", fontWeight: 800, lineHeight: 0.9,
            letterSpacing: "-.04em", color: "var(--ink)",
            display: "flex", alignItems: "baseline", gap: 14,
            fontVariantNumeric: "tabular-nums",
          }}>
            <span style={{ fontSize: 120 }}>{timeStr}</span>
            <span style={{ fontSize: 30, fontWeight: 700, color: "var(--n-400)" }}>{ampm}</span>
          </div>
          <div style={{ fontSize: 16.5, color: "var(--sub)", marginTop: 24, maxWidth: 380, lineHeight: 1.55 }}>
            Enter your passcode, then clock in for your shift or open the inventory manager.
          </div>
        </div>

        {/* Right: numpad card */}
        <div style={{
          width: 430, flexShrink: 0,
          background: "var(--surface)",
          border: "1px solid var(--line)",
          borderRadius: "var(--r-xl)",
          boxShadow: "var(--shadow-sm)",
          padding: "30px 30px 28px",
        }}>
          <div style={{ fontFamily: "var(--font-head)", fontSize: 20, fontWeight: 800, color: "var(--ink)", letterSpacing: "-.02em" }}>
            Enter passcode
          </div>
          <div style={{ fontSize: 13.5, color: "var(--sub)", marginTop: 3 }}>
            4 digits for staff · 6 for owner · ဖြည့်ပါ
          </div>

          {/* Dots */}
          <div style={{ display: "flex", gap: 13, justifyContent: "center", margin: "24px 0 10px" }}>
            {dots.map((filled, i) => (
              <span key={i} style={{
                width: filled ? 16 : 13, height: filled ? 16 : 13,
                borderRadius: 99,
                background: filled ? "var(--primary)" : "transparent",
                border: filled ? "none" : "2px solid var(--n-300)",
                transition: "all .12s",
              }} />
            ))}
          </div>

          {/* Error */}
          <div style={{ minHeight: 30, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 8 }}>
            {error && (
              <span style={{ color: "var(--danger)", fontSize: 13.5, textAlign: "center" }}>
                {error}
              </span>
            )}
          </div>

          {/* Numpad */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 11, marginBottom: 20 }}>
            {keys.map(k => {
              const isSoft = k === "clear" || k === "back";
              return (
                <button
                  key={k}
                  onClick={() => press(k)}
                  disabled={!!loading}
                  style={{
                    height: 66, borderRadius: 12,
                    background: isSoft ? "transparent" : "var(--surface)",
                    border: isSoft ? "none" : "1px solid var(--line)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    cursor: "pointer",
                    fontFamily: "var(--font-head)", fontSize: 26, fontWeight: 700,
                    color: "var(--ink)",
                    boxShadow: isSoft ? "none" : "var(--shadow-sm)",
                    transition: "background .1s",
                  }}
                  onMouseDown={e => e.currentTarget.style.background = "var(--blue-50)"}
                  onMouseUp={e => e.currentTarget.style.background = isSoft ? "transparent" : "var(--surface)"}
                  onMouseLeave={e => e.currentTarget.style.background = isSoft ? "transparent" : "var(--surface)"}
                >
                  {k === "clear" ? (
                    <span style={{ fontFamily: "var(--font-body)", fontSize: 15, fontWeight: 600, color: "var(--sub)" }}>
                      Clear
                    </span>
                  ) : k === "back" ? (
                    <BackspaceIcon />
                  ) : k}
                </button>
              );
            })}
          </div>

          {/* Action buttons */}
          <div style={{ display: "flex", gap: 11 }}>
            <ActionBtn
              onClick={() => submit("attendance")}
              disabled={!canSubmit}
              bg="var(--accent)"
              icon={<FiClock size={20} />}
            >
              {loading === "attendance" ? "…" : "Clock In / Out"}
            </ActionBtn>
            <ActionBtn
              onClick={() => submit("inventory")}
              disabled={!canSubmit}
              bg="var(--primary)"
              icon={<FiPackage size={20} />}
            >
              {loading === "inventory" ? "…" : "Inventory"}
            </ActionBtn>
          </div>
        </div>
      </div>
    </div>
  );
}

function ActionBtn({ onClick, disabled, bg, icon, children }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        flex: 1, height: 58,
        borderRadius: "var(--r-sm)",
        background: bg,
        color: "#fff", border: "none",
        fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 15.5,
        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.4 : 1,
        transition: "filter .12s",
      }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.filter = "brightness(.93)"; }}
      onMouseLeave={e => { e.currentTarget.style.filter = "none"; }}
    >
      {icon}
      {children}
    </button>
  );
}
